// Chord Display — virtual piano + MIDI input + chord detection
const MIDI_EVENT_NOTE_ON = 0x90;
const MIDI_EVENT_NOTE_OFF = 0x80;

const NOTE_NAMES: string[] = [
	"C",
	"C#",
	"D",
	"D#",
	"E",
	"F",
	"F#",
	"G",
	"G#",
	"A",
	"A#",
	"B",
];

const preferFlats = false; // TODO: add preference button
const sharpToFlat: Record<string, string> = {
	"C#": "Db",
	"D#": "Eb",
	"F#": "Gb",
	"G#": "Ab",
	"A#": "Bb",
};

function convertNoteEnharmonic(note: string): string {
	return preferFlats && sharpToFlat[note] ? sharpToFlat[note] : note;
}

interface SplitChord {
	root: string;
	quality: string;
}

function splitChordSymbol(chordSymbol: string): SplitChord {
	const match = chordSymbol.match(/^([A-G](?:♯|#)?)(.*)$/);
	return match
		? { root: match[1], quality: match[2] }
		: { root: chordSymbol, quality: "" };
}

function convertChordSymbol(chordSymbol: string): string {
	const parts = chordSymbol.split("/");
	const main = parts[0];
	const split = splitChordSymbol(main);
	const newRoot = convertNoteEnharmonic(split.root);
	let newChord = newRoot + split.quality;
	if (parts.length > 1) {
		const bass = parts[1];
		newChord += "/" + convertNoteEnharmonic(bass);
	}
	return newChord;
}

function getChordRoot(chordSymbol: string): string {
	return splitChordSymbol(chordSymbol).root;
}

function isSubset(subset: number[], set: number[]): boolean {
	return subset.every((val) => set.includes(val));
}

interface ChordCandidate {
	chord: string;
	priority: number;
	matchCount: number;
}

function detectChord(notes: string[]): ChordCandidate[] | null {
	const normalized = notes.map((note) => note.replace(/[0-9]/g, ""));
	const semitones: Record<string, number> = {
		C: 0,
		"C#": 1,
		D: 2,
		"D#": 3,
		E: 4,
		F: 5,
		"F#": 6,
		G: 7,
		"G#": 8,
		A: 9,
		"A#": 10,
		B: 11,
	};

	interface ChordFormula {
		symbol: string;
		intervals: number[];
		priority: number;
	}

	const chordFormulas: ChordFormula[] = [
		{ symbol: "aug", intervals: [4, 8], priority: 8 },
		{ symbol: "dim", intervals: [3, 6], priority: 9 },
		{ symbol: "add9", intervals: [2, 4, 7], priority: 9 },
		{ symbol: "madd9", intervals: [2, 3, 7], priority: 9 },
		{ symbol: "add4", intervals: [4, 5, 7], priority: 9 },
		{ symbol: "m", intervals: [3, 7], priority: 12 },
		{ symbol: "sus4", intervals: [5, 7], priority: 12 },
		{ symbol: "sus2", intervals: [2, 7], priority: 12 },
		{ symbol: "", intervals: [4, 7], priority: 13 },

		{ symbol: "6(no5)", intervals: [4, 9], priority: 5 },
		{ symbol: "6", intervals: [4, 7, 9], priority: 2 },
		{ symbol: "m6", intervals: [3, 7, 8], priority: 2 },
		{ symbol: "m7", intervals: [3, 7, 10], priority: 2 },
		{ symbol: "m7(no5)", intervals: [3, 10], priority: 4 },
		{ symbol: "7", intervals: [4, 7, 10], priority: 3 },
		{ symbol: "7(no3)", intervals: [7, 10], priority: 5 },
		{ symbol: "7(no5)", intervals: [4, 10], priority: 4 },
		{ symbol: "7sus4", intervals: [5, 7, 10], priority: 10 },

		{ symbol: "maj7", intervals: [4, 7, 11], priority: 4 },
		{ symbol: "maj7(no5)", intervals: [4, 11], priority: 4 },
		{ symbol: "mM7", intervals: [3, 7, 11], priority: 8 },
		{ symbol: "mM9", intervals: [2, 3, 7, 11], priority: 10 },

		{ symbol: "7-5", intervals: [4, 6, 10], priority: 8 },
		{ symbol: "7+5", intervals: [4, 8, 10], priority: 10 },
		{ symbol: "7+11", intervals: [4, 6, 7, 10], priority: 10 },

		{ symbol: "aug7", intervals: [4, 8, 10], priority: 8 },
		{ symbol: "dim7", intervals: [3, 6, 9], priority: 9 },
		{ symbol: "m7b5", intervals: [3, 6, 10], priority: 9 },

		{ symbol: "maj9", intervals: [2, 4, 7, 11], priority: 5 },
		{ symbol: "maj9(no5)", intervals: [2, 4, 11], priority: 5 },
		{ symbol: "maj9+11", intervals: [2, 4, 6, 7, 11], priority: 10 },

		{ symbol: "m9", intervals: [2, 3, 7, 10], priority: 5 },
		{ symbol: "m9(no5)", intervals: [2, 3, 10], priority: 5 },
		{ symbol: "9", intervals: [2, 4, 7, 10], priority: 5 },
		{ symbol: "9(no5)", intervals: [2, 4, 10], priority: 5 },
		{ symbol: "9sus4", intervals: [2, 5, 7, 10], priority: 10 },
		{ symbol: "9+11", intervals: [2, 4, 6, 7, 10], priority: 10 },
		{ symbol: "13+11", intervals: [4, 6, 7, 10], priority: 10 },
		{ symbol: "7-9", intervals: [1, 4, 7, 10], priority: 10 },
		{ symbol: "7+9", intervals: [3, 4, 7, 10], priority: 10 },

		{ symbol: "6add9", intervals: [2, 4, 7, 9], priority: 10 },

		{ symbol: "maj11", intervals: [2, 4, 5, 7, 11], priority: 10 },
		{ symbol: "maj11(no5)", intervals: [2, 4, 5, 11], priority: 10 },

		{ symbol: "m11", intervals: [2, 3, 5, 7, 10], priority: 5 },
		{ symbol: "m11(no5)", intervals: [2, 3, 5, 10], priority: 5 },

		{ symbol: "11", intervals: [2, 4, 5, 7, 10], priority: 5 },
		{ symbol: "11(no5)", intervals: [2, 4, 5, 10], priority: 5 },

		{ symbol: "maj13", intervals: [2, 4, 5, 7, 9, 11], priority: 5 },
		{ symbol: "maj13(no5)", intervals: [2, 4, 5, 9, 11], priority: 5 },
		{ symbol: "maj13+11", intervals: [2, 4, 6, 7, 9, 11], priority: 10 },

		{ symbol: "m13", intervals: [2, 3, 5, 7, 9, 10], priority: 5 },
		{ symbol: "m13(no5)", intervals: [2, 3, 5, 9, 10], priority: 5 },

		{ symbol: "13", intervals: [2, 4, 5, 7, 9, 10], priority: 5 },
		{ symbol: "13(no5)", intervals: [2, 4, 5, 9, 10], priority: 5 },
		{ symbol: "13-9", intervals: [1, 4, 5, 7, 9, 10], priority: 10 },
		{ symbol: "13+11", intervals: [2, 4, 6, 7, 9, 10], priority: 10 },
	];

	const candidates: ChordCandidate[] = [];
	const noteCount = normalized.length;

	for (let i = 0; i < noteCount; i++) {
		const root = normalized[i];
		const rootVal = semitones[root];
		console.assert(rootVal !== undefined);

		let intervals: number[] = [];
		for (let j = 0; j < noteCount; j++) {
			if (i === j) continue;
			const v = semitones[normalized[j]];
			if (v === undefined) continue;
			const diff = (v - rootVal + 12) % 12;
			intervals.push(diff);
		}

		intervals = Array.from(new Set(intervals)).sort((a, b) => a - b);

		chordFormulas.forEach((formula) => {
			if (noteCount !== formula.intervals.length + 1) return;
			if (
				isSubset(formula.intervals, intervals) &&
				intervals.length === formula.intervals.length
			) {
				const chordString =
					formula.symbol === "" ? root : root + formula.symbol;
				candidates.push({
					chord: chordString,
					priority: formula.priority,
					matchCount: formula.intervals.length,
				});
			}
		});
	}

	if (candidates.length === 0) return null;

	candidates.sort((a, b) => {
		if (a.matchCount !== b.matchCount) return b.matchCount - a.matchCount;
		return a.priority - b.priority;
	});

	const unique: ChordCandidate[] = [];
	candidates.forEach((c) => {
		if (!unique.some((u) => u.chord === c.chord)) unique.push(c);
	});
	return unique;
}

const chordCommonness: Record<string, number> = {
	"": 1,
	m: 2,
	maj7: 3,
	"7": 4,
	m7: 4,
	"6": 4,
	m6: 5,
	sus2: 10,
	sus4: 10,
	aug: 11,
	dim: 11,
	"6(no5)": 12,
	"m7(no5)": 13,
	"7(no3)": 15,
	"7(no5)": 13,
};

function getCommonness(chordSymbol: string): number {
	const split = splitChordSymbol(chordSymbol);
	const quality = split.quality;
	return Object.hasOwn(chordCommonness, quality)
		? chordCommonness[quality]
		: 100;
}

// --- State ---
let midiAccess: MIDIAccess | null = null;
// midiInput is used in initMIDI below
let isMidiConnected = false;
const activeNotes = new Set<number>(); // currently pressed MIDI note numbers
let chordTimeout: ReturnType<typeof setTimeout> | null = null;
const CHORD_DEBOUNCE_MS = 150;

// --- DOM refs (set in initChordDisplay) ---
let keysContainer: HTMLElement | null = null;
let chordNameEl: HTMLElement | null = null;
let keyLabelEl: HTMLElement | null = null;
let midiStatusEl: HTMLElement | null = null;
let connectBtn: HTMLButtonElement | null = null;
let fullscreenBtn: HTMLButtonElement | null = null;
let chordAppEl: HTMLElement | null = null;

// --- Piano building ---
function buildPiano(): void {
	if (!keysContainer) return;

	const startNote = 21; // A0
	const endNote = 108; // C8
	const layout = [
		false,
		true,
		false,
		false,
		true,
		false,
		true,
		false,
		false,
		true,
		false,
		true,
	];

	for (let midiNote = startNote; midiNote <= endNote; midiNote++) {
		const noteName = NOTE_NAMES[midiNote % 12];
		const isBlackKey = layout[(midiNote - startNote) % layout.length];

		const keyEl = document.createElement("div");
		keyEl.className = `key ${isBlackKey ? "black" : "white"}`;
		keyEl.dataset.midiNote = midiNote.toString();
		keyEl.title = `${noteName} ${Math.floor((midiNote - 12) / 12)}`;

		// Click to simulate press (for testing without MIDI)
		keyEl.addEventListener("mouseup", () => toggleNote(midiNote));

		keysContainer.appendChild(keyEl);
	}
}

function toggleNote(note: number): void {
	if (activeNotes.has(note)) {
		deactivateNote(note);
	} else {
		activateNote(note);
	}
}

function activateNote(note: number): void {
	activeNotes.add(note);
	updateKeyHighlight(note, true);
	checkChord();
}

function deactivateNote(note: number): void {
	activeNotes.delete(note);
	updateKeyHighlight(note, false);
	checkChord();
}

function updateKeyHighlight(note: number, highlighted: boolean): void {
	const el = document.querySelector(`.key[data-midi-note="${note}"]`);
	if (!el) return;
	if (highlighted) el.classList.add("highlighted");
	else el.classList.remove("highlighted");
}

function useUnicodeIncidentals(str: string): string {
	return str.replaceAll("#", "♯").replaceAll("b", "♭");
}

// --- Chord detection ---
function checkChord(): void {
	// Clear previous timeout
	if (chordTimeout) clearTimeout(chordTimeout);

	const notes = [...activeNotes];
	if (notes.length === 0) {
		chordNameEl!.textContent = "—";
		keyLabelEl!.textContent = "";
		return;
	}

	// Convert MIDI note numbers to note strings for detection
	const noteStrings = notes.map((n) => {
		const octave = Math.floor((n - 12) / 12);
		const letterIndex = n % 12;
		return NOTE_NAMES[letterIndex] + (octave + 1);
	});

	chordTimeout = setTimeout(() => {
		const candidates = detectChord(noteStrings);
		if (!candidates || candidates.length === 0) {
			chordNameEl!.textContent = "—";
			keyLabelEl!.textContent = "";
		} else {
			notes.sort((a, b) => a - b);
			const rootNote = notes[0];
			const bassLetterIndex = rootNote % 12;
			const bass = NOTE_NAMES[bassLetterIndex];

			candidates.sort(
				(a, b) => getCommonness(a.chord) - getCommonness(b.chord),
			);

			const displayCandidates = candidates.map((obj) => {
				let chordName = obj.chord;
				if (getChordRoot(chordName) !== bass) {
					chordName = chordName + "/" + bass;
				}
				return convertChordSymbol(chordName);
			});

			const top = displayCandidates[0];
			if (displayCandidates.length > 1) {
				chordNameEl!.textContent = `${top}, (${displayCandidates.slice(1).join(", ")})`;
			} else {
				chordNameEl!.textContent = top;
			}

			keyLabelEl!.textContent = useUnicodeIncidentals(
				notes
					.map((n) => `${NOTE_NAMES[n % 12]}${Math.floor((n - 12) / 12) + 1}`)
					.join(", "),
			);
			chordNameEl!.textContent = useUnicodeIncidentals(
				chordNameEl!.textContent,
			);
		}
	}, CHORD_DEBOUNCE_MS);
}

// --- MIDI connection ---
async function initMIDI(): Promise<void> {
	if (isMidiConnected) return;

	if (midiAccess == null) {
		try {
			midiAccess = await navigator.requestMIDIAccess();
		} catch (e: unknown) {
			const err = e as Error;
			midiStatusEl!.textContent = `Access denied: ${err.message}`;
			return;
		}
	}

	try {
		const devices = midiAccess.inputs;

		if (!devices.size) {
			midiStatusEl!.textContent = "No MIDI input devices found.";
			return;
		}

		let localMidiInput: MIDIInput | null = null;
		for (const entry of midiAccess.inputs) {
			localMidiInput = entry[1];
			break;
		}

		isMidiConnected = true;
		midiStatusEl!.textContent = `Connected: ${localMidiInput?.name ?? "Unknown"}`;
		connectBtn!.disabled = true;

		localMidiInput!.onmidimessage = (event: MIDIMessageEvent) => {
			if (!event.data) return;
			const midiEvent = event.data[0] & 0xf0;
			const note = event.data[1];
			// const velocity = event.data[2];

			if (midiEvent === MIDI_EVENT_NOTE_ON) {
				activateNote(note);
			} else if (midiEvent === MIDI_EVENT_NOTE_OFF) {
				deactivateNote(note);
			}
		};

		console.log("MIDI connected successfully!");
	} catch (err: unknown) {
		connectBtn!.disabled = true;
		const error = err as Error;
		console.error("MIDI connection failed:", error);
	}
}

function toggleFullscreen(): void {
	const app = chordAppEl;
	if (!app) {
		return
	};

	if (document.fullscreenElement) {
		document.exitFullscreen();
	} else {
		app
			.requestFullscreen()
			.catch((err: unknown) => {
				console.error("Fullscreen request failed:", err);
			});
	}
}

// --- Public init function ---
export function initChordDisplay(): void {
	keysContainer = document.getElementById("keys-container");
	chordNameEl = document.getElementById("chord-name");
	keyLabelEl = document.getElementById("key-label");
	midiStatusEl = document.getElementById("midi-status-text");
	connectBtn = document.getElementById(
		"connect-btn",
	) as HTMLButtonElement | null;
	fullscreenBtn = document.getElementById(
		"fullscreen-btn",
	) as HTMLButtonElement | null;
	chordAppEl = document.querySelector(".chord-app") as HTMLElement | null;

	buildPiano();

	if (connectBtn) {
		connectBtn.addEventListener("click", initMIDI);
	}

	if (fullscreenBtn && chordAppEl) {
		fullscreenBtn.addEventListener("click", toggleFullscreen);
	}
}

document.addEventListener("DOMContentLoaded", () => {
	initChordDisplay();
});
